import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import Button from '@/components/button'
import Slider from '@/components/slider'
import {Text} from '@/components/text'
import useMediaQuery from '@/hooks/use-media-query'
import {useGlobalStrings} from '@/providers/global-strings'
import {Section, BackgroundWrapper, Layout} from '@/styles/grid'
import {radii, tokens} from '@/theme'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import mq from '@/utils/media-query'
import {scrollToTop} from '@/utils/scroll'

import type {BillsCalculatorFragment} from '@/graphql-types'

import calculateResults from './calculate-results'

const mobileBreakpoint = 'sm'

export interface State {
    numOfInvoicePaidPerMonth: number
    averageTimeToProcess: number
    numOfPeopleInvolved: number
}

enum ActionType {
    NUM_INVOICE_PAID_PER_MONTH_UPDATED,
    AVERAGE_TIME_TO_PAY_UPDATED,
    NUM_OF_PEOPLE_INVOLVED_UPDATED,
}

type Action = {
    type:
        | ActionType.NUM_INVOICE_PAID_PER_MONTH_UPDATED
        | ActionType.AVERAGE_TIME_TO_PAY_UPDATED
        | ActionType.NUM_OF_PEOPLE_INVOLVED_UPDATED
    value: number
}

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case ActionType.NUM_INVOICE_PAID_PER_MONTH_UPDATED: {
            return {
                ...state,
                numOfInvoicePaidPerMonth: action.value,
            }
        }

        case ActionType.AVERAGE_TIME_TO_PAY_UPDATED: {
            return {
                ...state,
                averageTimeToProcess: action.value,
            }
        }

        case ActionType.NUM_OF_PEOPLE_INVOLVED_UPDATED: {
            return {
                ...state,
                numOfPeopleInvolved: action.value,
            }
        }

        default: {
            throw new Error('Unrecognized action')
        }
    }
}

interface ValueRange {
    min: number
    max: number
}

const valueRange: Record<string, ValueRange> = {
    numOfInvoicePaidPerMonth: {
        min: 0,
        max: 500,
    },
    averageTimeToProcess: {
        min: 0.5,
        max: 10,
    },
    numOfPeopleInvolved: {
        min: 0,
        max: 10,
    },
}

const initialState: State = {
    numOfInvoicePaidPerMonth: 20,
    averageTimeToProcess: 2.5,
    numOfPeopleInvolved: 4,
}

interface Props {
    content: BillsCalculatorFragment
}

export const BillsCalculator = ({content}: Props) => {
    const [state, dispatch] = React.useReducer(reducer, initialState)

    if (!content) {
        return handleNoPrismicData()
    }

    return (
        <Wrapper as={Section} $variant="two-asymmetrical">
            <QuestionsSection state={state} dispatch={dispatch} content={content} />
            <ResultsSection results={calculateResults(state)} content={content} />
        </Wrapper>
    )
}

interface QuestionsSectionProps {
    state: State
    dispatch: React.Dispatch<Action>
    content: BillsCalculatorFragment
}

const QuestionsSection = ({state, dispatch, content}: QuestionsSectionProps) => {
    const isSmallScreen = useMediaQuery.max(mobileBreakpoint)
    const numberOfInvoices = content.data.num_invoices[0]
    const averageTime = content.data.average_time_to_process[0]
    const numOfPeopleInvolved = content.data.num_of_people_involved[0]

    return (
        <Questions>
            <Text as="h2" $variant="heading-base">
                {content.data.section_title}
            </Text>

            <Question>
                <Label id="question-1" forwardedAs="label" $variant="paragraph-large">
                    {numberOfInvoices.label}
                </Label>

                <Slider
                    min={valueRange.numOfInvoicePaidPerMonth.min}
                    max={valueRange.numOfInvoicePaidPerMonth.max}
                    icons={
                        isSmallScreen
                            ? undefined
                            : [
                                  numberOfInvoices.icon1,
                                  numberOfInvoices.icon2,
                                  numberOfInvoices.icon3,
                              ]
                    }
                    aria-labelledby="question-1"
                    withNumberInput
                    value={state.numOfInvoicePaidPerMonth}
                    onChange={(value) => {
                        dispatch({
                            type: ActionType.NUM_INVOICE_PAID_PER_MONTH_UPDATED,
                            value,
                        })
                    }}
                />
            </Question>

            <Question>
                <Label id="question-2" forwardedAs="label" $variant="paragraph-large">
                    {averageTime.label}
                </Label>

                <Slider
                    min={valueRange.averageTimeToProcess.min}
                    max={valueRange.averageTimeToProcess.max}
                    step={0.5}
                    icons={
                        isSmallScreen
                            ? undefined
                            : [averageTime.icon3, averageTime.icon2, averageTime.icon1]
                    }
                    aria-labelledby="question-2"
                    withNumberInput
                    value={state.averageTimeToProcess}
                    onChange={(value) => {
                        dispatch({
                            type: ActionType.AVERAGE_TIME_TO_PAY_UPDATED,
                            value,
                        })
                    }}
                />
            </Question>

            <Question>
                <Label id="question-3" forwardedAs="label" $variant="paragraph-large">
                    {numOfPeopleInvolved.label}
                </Label>

                <Slider
                    min={valueRange.numOfPeopleInvolved.min}
                    max={valueRange.numOfPeopleInvolved.max}
                    icons={
                        isSmallScreen
                            ? undefined
                            : [
                                  numOfPeopleInvolved.icon1,
                                  numOfPeopleInvolved.icon2,
                                  numOfPeopleInvolved.icon3,
                              ]
                    }
                    aria-labelledby="question-3"
                    withNumberInput
                    value={state.numOfPeopleInvolved}
                    onChange={(value) => {
                        dispatch({
                            type: ActionType.NUM_OF_PEOPLE_INVOLVED_UPDATED,
                            value,
                        })
                    }}
                />
            </Question>
        </Questions>
    )
}

interface ResultsSectionProps {
    results: {
        invoicesTake: number
        withPleoBills: number
        timeSavedWithPleoBills: number
    }
    content: BillsCalculatorFragment
}

const ResultsSection = ({results, content}: ResultsSectionProps) => {
    const resultsContent = content.data.results[0]
    const globalStrings = useGlobalStrings()

    return (
        <StickyWrapper>
            <ResultsSectionContainer>
                {content.data.section_description && (
                    <Text $variant="paragraph-large" $color="muted">
                        {content.data.section_description}
                    </Text>
                )}

                <Results $color="pink">
                    <ResultHeader forwardedAs="h3" $variant="spezia">
                        {resultsContent.results_title}
                    </ResultHeader>

                    <ResultLine>
                        <Text $variant="paragraph-large" as="h4">
                            {resultsContent.invoices_take}
                        </Text>

                        <ResultOutput $variant="heading-base" $color="muted" aria-hidden="true">
                            <span>{results.invoicesTake <= 0 ? '-' : results.invoicesTake}</span>{' '}
                            {globalStrings.hours}
                        </ResultOutput>
                    </ResultLine>

                    <ResultLine>
                        <Text $variant="paragraph-large" as="h4">
                            {resultsContent.with_pleo_bills}
                        </Text>

                        <ResultOutput $variant="heading-base" $color="muted" aria-hidden="true">
                            <span>{results.withPleoBills <= 0 ? '-' : results.withPleoBills}</span>{' '}
                            {globalStrings.hours}
                        </ResultOutput>
                    </ResultLine>

                    <ResultLine>
                        <Text $variant="paragraph-large" as="h4">
                            {resultsContent.time_saved}
                        </Text>

                        <ResultOutput $variant="heading-base" $color="muted" aria-hidden="true">
                            <span>
                                {results.timeSavedWithPleoBills <= 0
                                    ? '-'
                                    : results.timeSavedWithPleoBills}
                            </span>{' '}
                            {globalStrings.hours}
                        </ResultOutput>
                    </ResultLine>
                </Results>

                <Button onClick={() => scrollToTop(undefined, 'smooth')}>
                    {content.data.button_cta_text}
                </Button>
            </ResultsSectionContainer>
        </StickyWrapper>
    )
}

const Wrapper = styled(Layout)`
    display: grid;

    @media (min-width: 768px) and (max-height: 768px) {
        padding-top: ${tokens.spacing40};
        padding-bottom: ${tokens.spacing40};
    }

    ${mq.max(mobileBreakpoint)} {
        row-gap: ${tokens.spacing60};
    }
`

const StickyWrapper = styled.div`
    position: sticky;
    top: 85px;
`

const ResultsSectionContainer = styled.div`
    display: grid;
    align-content: start;
    row-gap: ${tokens.spacing40};
    height: 100%;

    ${mq.min(mobileBreakpoint)} {
        justify-items: start;
    }
`

const ResultHeader = styled(Text)`
    ${mq.max(mobileBreakpoint)} {
        text-align: center;
    }
`

const Results = styled(BackgroundWrapper)`
    display: grid;
    width: 100%;
    padding: ${tokens.spacing40};
    overflow: hidden;
    border-radius: ${radii.arc20};
    gap: ${tokens.spacing32};
    grid-template-columns: 1fr;

    ${mq.max(mobileBreakpoint)} {
        padding: ${tokens.spacing20};
    }

    @media (min-width: 768px) and (max-height: 768px) {
        padding: ${tokens.spacing20};
    }
`

const ResultLine = styled.div`
    display: grid;
    grid-template-columns: repeat(2, auto);
    justify-content: space-between;
    align-items: center;
    gap: ${tokens.spacing10};

    ${mq.max('md')} {
        grid-template-columns: 1fr;
        justify-items: left;
        gap: ${tokens.spacing10};
    }

    ${mq.max(mobileBreakpoint)} {
        justify-items: center;
    }
`

const ResultOutput = styled(Text)`
    white-space: nowrap;
`

const Label = styled(Text)`
    margin-bottom: ${tokens.spacing10};
`

const Questions = styled.div`
    display: grid;
    grid-template-rows: 1fr auto;
    row-gap: ${tokens.spacing60};
    height: 100%;
`

const Question = styled.div`
    display: grid;
    row-gap: ${tokens.spacing4};
`

export const billsCalculatorFragment = graphql`
    fragment BillsCalculator on PrismicBillsCalculator {
        type
        data {
            section_title
            section_description
            num_invoices {
                label
                icon1 {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                icon2 {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                icon3 {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
            }
            average_time_to_process {
                label
                icon1 {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                icon2 {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                icon3 {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
            }
            num_of_people_involved {
                label
                icon1 {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                icon2 {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                icon3 {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
            }
            results {
                results_title
                invoices_take
                with_pleo_bills
                time_saved
            }
            button_cta_text
        }
    }
`

export default BillsCalculator
